import { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CreateWebsiteBlog,
  UpdateWebsiteBlog,
  FindWebsiteBlogById
} from '../graphql/website-blog.graphql'
import { Button, message, Form, Input, Card, Space, Typography, Spin, Select } from 'antd'
import ContentEditor from './ContentEditor'
import { ArrowLeftOutlined, FileSearchOutlined, SaveOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { BlogPreview } from './BlogPreview'
import { WebsiteBlogInput, WebsiteBlogType } from '../type/website-blog'

const { Title } = Typography

export const BlogForm: FC = () => {
  const navigate = useNavigate()
  const { id: idParam } = useParams<{ id: string }>()
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [isLoading, setIsLoading] = useState(false)
  const id = Number(idParam)
  const isEditing = Boolean(id)
  const [openPreview, setOpenPreview] = useState(false)
  const [content, setContent] = useState<any>(isEditing ? undefined : {blogs: []})

  const { loading: fetchingBlog } = useQuery(FindWebsiteBlogById, {
    variables: {
      request: {
        id: id ?? 0,
        take: 1,
        skip: 0,
      },
    },
    skip: !isEditing,
    onCompleted: (result) => {
      const [entity] = result.websiteBlogsFind?.data ?? []

      if (!entity)
        return

      form.setFieldsValue({
        name: entity.name,
        type: entity.type,
        url: entity.url,
        metaTitle: entity.metaTitle,
        metaDescription: entity.metaDescription,
        content: entity.content,
      })

      setContent(entity.content)
    },
    onError: (error) => {
      messageApi.error('Failed to load blog: ' + error.message)
      handleCancel()
    }
  })

  const [create, { loading: creating }] = useMutation(CreateWebsiteBlog, {
    onCompleted: (result) => {
      messageApi.success('Blog created successfully!')
      navigate('/admin/blogs')
    },
    onError: (error) => {
      messageApi.error('Failed to create blog: ' + error.message)
    }
  })

  const [update, { loading: updating }] = useMutation(UpdateWebsiteBlog, {
    onCompleted: (result) => {
      messageApi.success('Blog updated successfully!')
      navigate('/admin/blogs')
    },
    onError: (error) => {
      messageApi.error('Failed to update blog: ' + error.message)
    }
  })

  const handleSave = async () => {
    try {
      setIsLoading(true)
      const values = await form.validateFields()
      console.log('Form values:', values)
      console.log('Content value:', values.content)

      const input: WebsiteBlogInput = {
        ...values,
        content,
      }

      if (isEditing) {
        await update({
          variables: {
            id,
            input,
          }
        })
      } else {
        await create({
          variables: {
            input,
          }
        })
      }
    } catch (error) {
      console.error('Form validation failed:', error)
      messageApi.error('Please check your form inputs')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/blogs')
  }

  const loading = creating || updating || isLoading

  if (fetchingBlog) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div
      className='width-100'
    >
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleCancel}
                disabled={loading}
              >
                {translate('back')}
              </Button>
              <Title level={2} style={{ margin: 0 }}>
                {isEditing ? 'Edit Blog' : 'Create New Blog'}
              </Title>
            </div>
            <Space>
              <Button
                icon={<FileSearchOutlined />}
                onClick={() => setOpenPreview(true)}
                size="large"
              >
                {translate('preview')}
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                size="large"
              >
                {translate('save')}
              </Button>
            </Space>
          </div>

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            size="large"
            style={{ width: '100%' }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
              {/* Left Column - Basic Info */}
              <div>
                <Form.Item
                  name="name"
                  label="Blog Title"
                  rules={[
                    { required: true, message: 'Please enter blog title' },
                    { min: 3, message: 'Title must be at least 3 characters' },
                    { max: 200, message: 'Title must be less than 200 characters' }
                  ]}
                >
                  <Input placeholder="Enter your blog title" />
                </Form.Item>

                <Form.Item
                  name="type"
                  label={translate('type')}
                  rules={[
                    { required: true, message: translate('type', 'is_required') },
                  ]}
                >
                  <Select
                    placeholder={translate('select')}
                    options={Object.values(WebsiteBlogType).map(type => ({ label: type, value: type }))}
                  />
                </Form.Item>

                <Form.Item
                  name="url"
                  label="URL Slug"
                  rules={[
                    { required: true, message: 'Please enter URL slug' },
                    { pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/, message: 'URL must be in kebab-case format (e.g., "my-blog-post")' },
                    { min: 2, message: 'URL must be at least 2 characters' },
                    { max: 100, message: 'URL cannot exceed 100 characters' }
                  ]}
                >
                  <Input placeholder="my-blog-post" />
                </Form.Item>

                <Form.Item
                  name="metaTitle"
                  label="Meta Title (SEO)"
                  rules={[
                    { required: true, message: 'Please enter meta title' },
                    { min: 10, message: 'Meta title should be at least 10 characters' },
                    { max: 60, message: 'Meta title should be less than 60 characters' }
                  ]}
                >
                  <Input placeholder="SEO title for search engines" />
                </Form.Item>

                <Form.Item
                  name="metaDescription"
                  label="Meta Description (SEO)"
                  rules={[
                    { required: true, message: 'Please enter meta description' },
                    { min: 50, message: 'Meta description should be at least 50 characters' },
                    { max: 160, message: 'Meta description should be less than 160 characters' }
                  ]}
                >
                  <Input.TextArea
                    placeholder="SEO description for search engines"
                    rows={3}
                    showCount
                    maxLength={160}
                  />
                </Form.Item>

              </div>

              {/* Right Column - Content Editor */}
              <div>
                <Form.Item
                  label="Blog Content"
                  required
                  style={{ marginBottom: 0 }}
                >
                  {content && (
                    <ContentEditor
                      value={content}
                      onChange={setContent}
                      placeholder="Start writing your blog content..."
                      style={{ minHeight: '500px' }}
                    />
                  )}
                </Form.Item>
              </div>
            </div>
            <Form.Item
              shouldUpdate
              noStyle
            >
              {({ getFieldValue }) => {
                return (
                  <BlogPreview
                    content={content}
                    open={openPreview}
                    onClose={() => setOpenPreview(false)}
                  />
                )
              }}
            </Form.Item>
          </Form>
        </Space>
      </Card>
      {contextHolder}
    </div>
  )
}

